import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import fetch from 'cross-fetch';
import { withTranslation } from 'react-i18next';
import smoothscroll from 'smoothscroll-polyfill';
import Loading from '../account_dashboard/Loading.jsx';

import NavBar from './NavBar.jsx';
import PlanSelector from './PlanSelector.jsx';

smoothscroll.polyfill();

/**
 * Sorts a given array of strings by corresponding weights.
 *
 * @param arr An array of strings that need to be sorted by some given weights
 * @param weights an array of objects that maps a string to a given weight
 * @returns The originally arr sorted based off the weights, lowest weight first
 */
function sortByWeight(arr, weights) {
  let weightedArray = arr.map(e => ({
    value: e,
    weight: weights[e] !== undefined ? weights[e] : 999,
  }));

  weightedArray.sort((e1, e2) => e1.weight - e2.weight);

  return weightedArray.map(e => e.value);
}

const PLAN_INTERVAL_SORT_WEIGHTS = {
  monthly: 0,
  annually: 1,
};

class PlanSelectForm extends Component {
  state = {
    isLoading: true,
    errorMessage: null,
    isNextLoading: false,
  }

  availablePlans = [];

  navBarRef = createRef();

  componentDidMount() {
    // Load countries list
    // Populate selected country if props.useGeoDefault is true
    this.loadAvailablePlans();
  }

  loadAvailablePlans = () => {
    const { deviceEmei } = this.props.values;
    const { i18n: { language } } = this.props;

    fetch(`/api/available-plans?imei=${deviceEmei}&locale=${language}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) {
          // Possibly due to user not logged in etc
          throw new Error('Unable to load available plans');
        }

        return response.json();
      })
      .then((json) => {
        const { selectedPlanInterval } = this.props.values;

        this.availablePlans = json;

        // Needed if say a different set of plans is loaded that doesn't match
        // the previously selected and stored interval selection state
        if (!this.availablePlanIntervalKeys().includes(selectedPlanInterval)) {
          this.handlePlanIntervalChange('monthly');
        }

        this.setState({
          isLoading: false,
        });
      })
      .catch((error) => {
        this.setState({
          errorMessage: error.message,
          isLoading: false,
        });
        throw error;
      });
  }

  availablePlanIntervalKeys = () => {
    const uniqueAvailPlans = new Set(this.availablePlans.map(plan => plan.interval));
    return sortByWeight(Array.from(uniqueAvailPlans), PLAN_INTERVAL_SORT_WEIGHTS);
  }

  availablePlanIntervals = () => {
    const { t } = this.props;

    const intervalKeys = this.availablePlanIntervalKeys();
    return intervalKeys.map(key => ({
      key,
      title: t(`plan_select_form.interval_${key}_title`),
      subtitle: t(`plan_select_form.interval_${key}_subtitle`),
    }));
  }

  nextDisabled = () => (this.props.values.selectedPlanId === null);

  handlePlanSelect = (plan) => {
    this.props.handleChange('selectedPlanId')({ target: { value: plan.id } });
    this.props.handleChange('devicePlatform')({ target: { value: plan.devicePlatform } });

    // Doesn't seem to work without the timeout
    setTimeout(() => {
      this.navBarRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }, 100);
  }

  handlePlanIntervalChange = (interval) => {
    // Ensure plan is unselected
    this.props.handleChange('selectedPlanId')({ target: { value: null } });
    this.props.handleChange('selectedPlanInterval')({ target: { value: interval } });
  }

  getAvailablePlans = (interval) => {
    return this.availablePlans.filter(plan => plan.interval === interval) || [];
  }

  renderContent = () => {
    const { t } = this.props;
    const { selectedPlanInterval, selectedPlanId, deviceModel } = this.props.values;
    const { onBack } = this.props;

    if (this.state.isLoading) {
      return <Loading />;
    }

    // 7Feb24: If user is trying to activate a OB22, we want to show them a special notification
    // - letting them know to contact support if they think they purchased an annual plan. 
    // https://shieldgpsgroup.slack.com/archives/C01J4T80C2G/p1707324504827239
    const showSupportPrompt = deviceModel === 'OB22';

    return (
      <React.Fragment>
        { showSupportPrompt && (
          <div className="alert alert-warning text-muted">
            {t('contact_us')} <a className="mailto" href="mailto:support@shieldgps.co">support@shieldgps.co</a>.
          </div>
        )}

        <PlanSelector
          plans={this.getAvailablePlans(selectedPlanInterval)}
          availablePlanIntervals={this.availablePlanIntervals()}
          selectedPlanInterval={selectedPlanInterval}
          selectedPlanId={selectedPlanId}
          onPlanSelect={this.handlePlanSelect}
          onPlanIntervalChange={this.handlePlanIntervalChange}
        />
        {this.renderErrorBox()}
        <div ref={this.navBarRef}>
          <NavBar
            onNext={this.createCheckoutSession}
            isLoading={this.state.isNextLoading}
            nextDisabled={this.nextDisabled()}
            onBack={onBack} />
        </div>
      </React.Fragment>
    );
  }

  createCheckoutSession = () => {
    const { deviceEmei, selectedPlanId } = this.props.values;
    const { encodeState } = this.props;

    const uiState = encodeState();
    this.setState({ isNextLoading: true });

    fetch('/sessions/me/checkout-sessions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        deviceEmei,
        planId: selectedPlanId,
        uiState,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          // Possibly due to user not logged in etc
          throw new Error('Unable to create checkout session');
        }

        return response.json();
      })
      .then((json) => {
        if (!json.success) {
          throw new Error(json.errorMessage);
        }

        window.location.replace(json.redirectUrl);
        this.setState({
          isLoading: false,
        });
      })
      .catch((error) => {
        this.setState({
          errorMessage: error.message,
          isNextLoading: false,
        });
        throw error;
      });
  }

  renderErrorBox = () => {
    if (this.state.errorMessage) {
      return (
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {this.state.errorMessage}
        </div>
      );
    }

    return false;
  };

  render() {
    const { t } = this.props;

    return (
      <div>
        <h2>{t('plan_select_form.title')}</h2>
        {this.renderContent()}
      </div>
    );
  }
}

PlanSelectForm.propTypes = {
  onBack: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  encodeState: PropTypes.func,
};


export { PlanSelectForm };
export default withTranslation('activation_wizard')(PlanSelectForm);
